import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, CreditCard, X, Loader2 } from 'lucide-react';
import { getUserTokenBalance } from '@/lib/tokens-api';
import { useAuth } from '@/context/AuthContext';
import { stripeProducts, type StripeProduct } from '@/stripe-config';
import { createCheckoutSession } from '@/lib/stripe';

interface CoinButtonProps {
  collapsed: boolean;
}

export default function CoinButton({ collapsed }: CoinButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [purchasingProductId, setPurchasingProductId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    console.log('[CoinButton] User:', user ? 'logged in' : 'not logged in');
    if (user) {
      fetchCredits();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCredits = async () => {
    console.log('[CoinButton] Fetching credits...');
    try {
      const balance = await getUserTokenBalance();
      console.log('[CoinButton] Balance fetched:', balance);
      setCredits(balance?.token_balance || 0);
    } catch (error) {
      console.error('[CoinButton] Error fetching credits:', error);
      setCredits(0);
    } finally {
      setLoading(false);
    }
  };

  const getCreditColor = () => {
    if (credits < 3) return 'text-red-600';
    if (credits <= 5) return 'text-yellow-600';
    return 'text-green-600';
  };

  const handleProductClick = async (product: StripeProduct) => {
    if (purchasingProductId) return;

    try {
      setPurchasingProductId(product.priceId);
      console.log('[CoinButton] Initiating purchase for:', product);

      const successUrl = `${window.location.origin}/dashboard?purchase=success`;
      const cancelUrl = `${window.location.origin}/dashboard?purchase=cancelled`;

      const { url } = await createCheckoutSession({
        price_id: product.priceId,
        success_url: successUrl,
        cancel_url: cancelUrl,
        mode: product.mode,
      });

      console.log('[CoinButton] Redirecting to checkout:', url);
      window.location.href = url;
    } catch (error) {
      console.error('[CoinButton] Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setPurchasingProductId(null);
    }
  };

  if (!user) {
    console.log('[CoinButton] No user, not rendering');
    return null;
  }

  console.log('[CoinButton] Rendering with credits:', credits, 'loading:', loading);

  const modalContent = (
    <AnimatePresence>
      {isModalOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => setIsModalOpen(false)}
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Purchase Charm Coins</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-3 overflow-y-auto max-h-[calc(90vh-180px)]">
                {stripeProducts.map((product, index) => {
                  const isPurchasing = purchasingProductId === product.priceId;

                  return (
                    <motion.button
                      key={product.priceId}
                      onClick={() => handleProductClick(product)}
                      disabled={isPurchasing || !!purchasingProductId}
                      whileHover={!isPurchasing && !purchasingProductId ? { scale: 1.02 } : {}}
                      whileTap={!isPurchasing && !purchasingProductId ? { scale: 0.98 } : {}}
                      className={`
                        w-full p-4 rounded-xl border-2 text-left
                        transition-all duration-300
                        ${isPurchasing || purchasingProductId
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:border-blue-500 hover:shadow-lg cursor-pointer'
                        }
                        ${index === 1
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">
                              {product.name}
                            </h3>
                            {index === 1 && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-blue-500 text-white rounded-full">
                                Popular
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {product.description}
                          </p>
                          <div className="flex items-center gap-2 text-yellow-600">
                            <Coins className="w-4 h-4" />
                            <span className="font-semibold text-sm">
                              {product.name}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-2">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">
                              {product.currencySymbol}{product.price.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              /month
                            </div>
                          </div>
                          <div className="p-2 rounded-lg bg-gray-100">
                            {isPurchasing ? (
                              <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                            ) : (
                              <CreditCard className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex items-start gap-2">
                  <svg
                    className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-xs text-gray-600">
                    Secure payment powered by Stripe. Your payment information is encrypted and secure.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`
          w-full flex items-center gap-3 p-3 rounded-xl
          bg-white/20 backdrop-blur-sm border border-yellow-200/30
          hover:bg-white/30 hover:border-yellow-300/50
          transition-all duration-300
          ${collapsed ? 'flex-col justify-center' : 'justify-between'}
        `}
        title={collapsed ? `${credits} Credits` : undefined}
      >
        <div className={`flex items-center gap-2 ${collapsed ? 'flex-col' : ''}`}>
          <Coins className="w-5 h-5 text-yellow-500 flex-shrink-0" />
          {!collapsed && (
            <span className="text-sm font-medium text-gray-700">Credits</span>
          )}
        </div>
        <div className={`font-bold ${getCreditColor()} ${collapsed ? 'text-xs' : 'text-sm'}`}>
          {loading ? '...' : credits}
        </div>
      </button>

      {createPortal(modalContent, document.body)}
    </>
  );
}
